import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Heart, Building2, Download, Users, Target, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { downloadCSV } from '@/components/utils/pdfGeneration';
import { format } from 'date-fns';

export default function PenetrationReports() {
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [penetrationData, setPenetrationData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (employees.length > 0 && enrollments.length > 0) {
      calculatePenetration();
    }
  }, [selectedEmployer, employees, enrollments, products, categories]);

  const loadData = async () => {
    try {
      const [employersData, employeesData, enrollmentsData, productsData, categoriesData] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.Employee.filter({ is_active: true }),
        api.entities.Enrollment.filter({ status: 'active' }),
        api.entities.BenefitProduct.list(),
        api.entities.BenefitCategory.list()
      ]);
      setEmployers(employersData);
      setEmployees(employeesData);
      setEnrollments(enrollmentsData);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePenetration = () => {
    const filteredEmployees = selectedEmployer === 'all'
      ? employees
      : employees.filter(e => e.employer_id === selectedEmployer);

    const filteredEnrollments = selectedEmployer === 'all'
      ? enrollments
      : enrollments.filter(e => e.employer_id === selectedEmployer);

    const totalEmployees = filteredEmployees.length;
    const enrolledEmployees = new Set(filteredEnrollments.map(e => e.employee_id)).size;
    const overallPenetration = totalEmployees > 0 ? (enrolledEmployees / totalEmployees) * 100 : 0;

    // Product Penetration
    const productPenetration = products.map(product => {
      const productEnrollments = filteredEnrollments.filter(e => e.product_id === product.id).length;
      const rate = totalEmployees > 0 ? (productEnrollments / totalEmployees) * 100 : 0;
      return {
        name: product.name,
        enrollments: productEnrollments,
        rate: rate,
        color: rate > 50 ? '#10b981' : rate > 25 ? '#3b82f6' : '#f59e0b'
      };
    }).sort((a, b) => b.rate - a.rate);

    // Category Penetration
    const categoryPenetration = categories.map(category => {
      const categoryProducts = products.filter(p => p.category_id === category.id);
      const categoryEnrollments = filteredEnrollments.filter(e => 
        categoryProducts.some(p => p.id === e.product_id)
      ).length;
      const rate = totalEmployees > 0 ? (categoryEnrollments / totalEmployees) * 100 : 0;
      return {
        name: category.name,
        rate: rate,
        enrollments: categoryEnrollments
      };
    }).filter(c => c.enrollments > 0).sort((a, b) => b.rate - a.rate);

    // Enrollment Status Breakdown
    const enrollmentStatuses = {
      enrolled: enrolledEmployees,
      notEnrolled: totalEmployees - enrolledEmployees,
      multipleProducts: filteredEnrollments.reduce((acc, enrollment) => {
        acc[enrollment.employee_id] = (acc[enrollment.employee_id] || 0) + 1;
        return acc;
      }, {})
    };

    const multipleProductCount = Object.values(enrollmentStatuses.multipleProducts).filter(count => count > 1).length;

    setPenetrationData({
      totalEmployees,
      enrolledEmployees,
      notEnrolled: totalEmployees - enrolledEmployees,
      overallPenetration,
      productPenetration,
      categoryPenetration,
      multipleProductCount,
      avgProductsPerEmployee: totalEmployees > 0 ? filteredEnrollments.length / totalEmployees : 0
    });
  };

  const exportPenetration = () => {
    const rows = [['Metric', 'Value']];
    rows.push(['Total Employees', penetrationData.totalEmployees]);
    rows.push(['Enrolled Employees', penetrationData.enrolledEmployees]);
    rows.push(['Overall Penetration Rate', `${penetrationData.overallPenetration?.toFixed(1)}%`]);
    rows.push(['Avg Products Per Employee', penetrationData.avgProductsPerEmployee?.toFixed(2)]);
    rows.push(['', '']);
    rows.push(['Product', 'Enrollments', 'Penetration Rate']);
    penetrationData.productPenetration?.forEach(p => {
      rows.push([p.name, p.enrollments, `${p.rate.toFixed(1)}%`]);
    });

    const csv = rows.map(row => row.join(',')).join('\n');
    downloadCSV(csv, `penetration_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Penetration Analysis</h1>
          <p className="text-slate-500 mt-1">
            Benefit enrollment penetration rates and participation metrics
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
            <SelectTrigger className="w-48">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Employers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employers</SelectItem>
              {employers.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportPenetration} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Overall Penetration */}
      <Card className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                <Target className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">Overall Penetration Rate</h3>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  {penetrationData.enrolledEmployees} of {penetrationData.totalEmployees} employees enrolled
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-emerald-700 dark:text-emerald-400">
                {penetrationData.overallPenetration?.toFixed(1)}%
              </p>
            </div>
          </div>
          <Progress value={penetrationData.overallPenetration} className="h-3" />
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-slate-500">Not Enrolled</p>
                <p className="text-2xl font-bold">{penetrationData.notEnrolled}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-sm text-slate-500">Multiple Products</p>
                <p className="text-2xl font-bold">{penetrationData.multipleProductCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-sm text-slate-500">Avg Products/Employee</p>
                <p className="text-2xl font-bold">{penetrationData.avgProductsPerEmployee?.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Product Penetration Chart */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card>
          <CardHeader>
            <CardTitle>Penetration by Product</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={penetrationData.productPenetration} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={150} stroke="#94a3b8" fontSize={12} />
                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                <Bar dataKey="rate" radius={[0, 4, 4, 0]}>
                  {penetrationData.productPenetration?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </motion.div>

      {/* Category Penetration */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card>
          <CardHeader>
            <CardTitle>Penetration by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {penetrationData.categoryPenetration?.map((category, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-700">{category.name}</span>
                    <span className="text-emerald-600 font-semibold">{category.rate.toFixed(1)}%</span>
                  </div>
                  <Progress value={category.rate} className="h-2" />
                  <p className="text-sm text-slate-500 mt-1">{category.enrollments} enrollments</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}